import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Activity, 
  Search, 
  Filter, 
  Calendar,
  User,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Download,
  RefreshCw
} from 'lucide-react';

interface ActivityLog {
  _id: string;
  userId: string;
  userEmail: string;
  action: string;
  details: any;
  ipAddress?: string;
  userAgent?: string;
  location?: {
    country?: string;
    city?: string;
    region?: string;
  };
  createdAt: string;
}

interface LogsResponse {
  logs: ActivityLog[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalLogs: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const AdminLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    userId: '',
    action: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 50
  });

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getActivityLogs(filters);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error: any) {
      setError(error.message || 'Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getActionBadge = (action: string) => {
    const actionColors: Record<string, string> = {
      'login': 'bg-green-100 text-green-800',
      'logout': 'bg-gray-100 text-gray-800',
      'signup': 'bg-blue-100 text-blue-800',
      'lead_scraped': 'bg-purple-100 text-purple-800',
      'email_sent': 'bg-orange-100 text-orange-800',
      'file_uploaded': 'bg-indigo-100 text-indigo-800',
      'ai_chat': 'bg-pink-100 text-pink-800',
      'account_blocked': 'bg-red-100 text-red-800',
      'account_unblocked': 'bg-green-100 text-green-800',
      'role_changed': 'bg-yellow-100 text-yellow-800',
      'user_deleted': 'bg-red-100 text-red-800',
      'user_promoted': 'bg-blue-100 text-blue-800',
      'admin_demoted': 'bg-orange-100 text-orange-800'
    };

    const color = actionColors[action] || 'bg-gray-100 text-gray-800';
    const label = action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <Badge className={color}>
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDetails = (details: any) => {
    if (!details || typeof details !== 'object') return '';
    
    const relevantKeys = ['targetUserEmail', 'reason', 'newRole', 'previousRole', 'endpoint', 'method'];
    const relevantDetails = Object.entries(details)
      .filter(([key]) => relevantKeys.includes(key))
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
    
    return relevantDetails || JSON.stringify(details).substring(0, 100);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Activity Logs</h1>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[...Array(10)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Activity Logs
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor user activities and system events
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={loadLogs}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              // Export logs as CSV
              const csvContent = "data:text/csv;charset=utf-8,"
                + "Date,User,Action,Details\n"
                + logs.map(log =>
                    `"${formatDate(log.createdAt)}","${log.userEmail}","${getActionLabel(log.action)}","${JSON.stringify(log.details).replace(/"/g, '""')}"`
                  ).join("\n");

              const encodedUri = encodeURI(csvContent);
              const link = document.createElement("a");
              link.setAttribute("href", encodedUri);
              link.setAttribute("download", `activity_logs_${new Date().toISOString().split('T')[0]}.csv`);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-red-800">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                User ID
              </label>
              <Input
                placeholder="User ID or email..."
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Action
              </label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Actions</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="signup">Signup</option>
                <option value="lead_scraped">Lead Scraped</option>
                <option value="email_sent">Email Sent</option>
                <option value="file_uploaded">File Uploaded</option>
                <option value="ai_chat">AI Chat</option>
                <option value="account_blocked">Account Blocked</option>
                <option value="account_unblocked">Account Unblocked</option>
                <option value="role_changed">Role Changed</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Start Date
              </label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                End Date
              </label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={() => setFilters(prev => ({ ...prev, page: 1 }))}
                className="w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Apply
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Activity Logs ({pagination?.totalLogs || 0})</span>
            <div className="text-sm text-gray-500">
              Page {pagination?.currentPage || 1} of {pagination?.totalPages || 1}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {logs.map((log) => (
              <div key={log._id} className="border border-border rounded-lg p-4 hover:bg-accent/5 bg-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getActionBadge(log.action)}
                      <span className="text-sm text-gray-600">
                        {formatDate(log.createdAt)}
                      </span>
                    </div>
                    
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{log.userEmail}</span>
                        <span className="text-sm text-gray-500">({log.userId})</span>
                      </div>
                      
                      {log.details && (
                        <div className="text-sm text-gray-600">
                          <strong>Details:</strong> {formatDetails(log.details)}
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        {log.ipAddress && (
                          <span>IP: {log.ipAddress}</span>
                        )}
                        {log.location?.country && (
                          <span>Location: {log.location.city}, {log.location.country}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {logs.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-500">
                No activity logs found matching your criteria
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {((pagination.currentPage - 1) * filters.limit) + 1} to{' '}
            {Math.min(pagination.currentPage * filters.limit, pagination.totalLogs)} of{' '}
            {pagination.totalLogs} logs
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            
            <span className="text-sm text-gray-600">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogsPage;
